/**
 * FSRS weight optimizer — Block G.
 *
 * Pure, platform-independent. Given a review history, finds FSRS-4.5 weights
 * that minimize RMSE between the algorithm's predicted retrievability and each
 * review's actual recall outcome (1 = remembered, 0 = forgot).
 *
 * Algorithm: numerical gradient descent with forward differences, 200
 * iterations, early stop when improvement < 1e-7.
 */

import {
  type Card,
  type FsrsParams,
  type Rating,
  DEFAULT_PARAMS,
  newCard,
  retrievability,
  review,
} from "./fsrs.js";

/** Minimal review shape — compatible with ReviewRecord from @music-app/persistence. */
export interface ReviewSample {
  itemId: string;
  /** ISO timestamp of when the review happened. */
  timestamp: string;
  rating: Rating;
  /** True if the user answered correctly. */
  correct: boolean;
}

export interface OptimizeResult {
  /** Optimized weights — pass to FsrsParams.w. */
  weights: number[];
  /** RMSE before optimization (with the initial/default weights). */
  baselineRmse: number;
  /** RMSE with the optimized weights. */
  rmse: number;
  /** Number of (predicted, actual) pairs the optimizer trained on. */
  trainingSize: number;
}

interface Pair {
  predicted: number;
  actual: number;
}

const W_MIN = 1e-3;
const W_MAX = 50;

/**
 * Replay each item's review history with `params` and collect
 * (predicted_retrievability, actual_recall) pairs from every non-initial review.
 */
function simulateTrajectories(samples: ReviewSample[], params: FsrsParams): Pair[] {
  const byItem = new Map<string, ReviewSample[]>();
  for (const s of samples) {
    let group = byItem.get(s.itemId);
    if (!group) {
      group = [];
      byItem.set(s.itemId, group);
    }
    group.push(s);
  }

  const pairs: Pair[] = [];

  for (const [, itemReviews] of byItem) {
    const sorted = [...itemReviews].sort((a, b) =>
      a.timestamp < b.timestamp ? -1 : a.timestamp > b.timestamp ? 1 : 0,
    );

    let card: Card = newCard(new Date(sorted[0]!.timestamp));

    for (const sample of sorted) {
      const reviewDate = new Date(sample.timestamp);

      if (card.state !== "new" && card.lastReview !== null) {
        const elapsedDays = Math.max(
          (reviewDate.getTime() - new Date(card.lastReview).getTime()) / 86_400_000,
          0,
        );
        const predicted = retrievability(card.stability, elapsedDays);
        pairs.push({ predicted, actual: sample.correct ? 1 : 0 });
      }

      card = review(card, sample.rating, reviewDate, params);
    }
  }

  return pairs;
}

function computeRMSE(pairs: Pair[]): number {
  if (pairs.length === 0) return 0;
  const mse = pairs.reduce((sum, p) => sum + (p.predicted - p.actual) ** 2, 0) / pairs.length;
  return Math.sqrt(mse);
}

/**
 * Optimize FSRS-4.5 weights for the given review history.
 *
 * @param reviews - All review records. Pass at least 1,000 for meaningful results.
 * @param initialWeights - Starting point for optimization. Defaults to FSRS-4.5 defaults.
 */
export function optimizeWeights(
  reviews: ReviewSample[],
  initialWeights?: readonly number[],
): OptimizeResult {
  const w: number[] = [...(initialWeights ?? DEFAULT_PARAMS.w)];
  const N = w.length;

  const basePairs = simulateTrajectories(reviews, { ...DEFAULT_PARAMS, w });
  const trainingSize = basePairs.length;
  const baselineRmse = computeRMSE(basePairs);

  if (trainingSize === 0) {
    return { weights: [...w], baselineRmse: 0, rmse: 0, trainingSize: 0 };
  }

  let currentLoss = baselineRmse;
  const eps = 1e-4;
  const lr = 0.01;
  const maxIter = 200;

  for (let iter = 0; iter < maxIter; iter++) {
    // Numerical gradient via forward differences.
    const grad = new Array<number>(N).fill(0);
    for (let i = 0; i < N; i++) {
      const wPlus = [...w];
      wPlus[i] = (wPlus[i]! + eps);
      const lossPlus = computeRMSE(
        simulateTrajectories(reviews, { ...DEFAULT_PARAMS, w: wPlus }),
      );
      grad[i] = (lossPlus - currentLoss) / eps;
    }

    // Gradient step + clamp.
    for (let i = 0; i < N; i++) {
      w[i] = Math.min(Math.max(w[i]! - lr * grad[i]!, W_MIN), W_MAX);
    }

    const newLoss = computeRMSE(simulateTrajectories(reviews, { ...DEFAULT_PARAMS, w }));
    if (Math.abs(currentLoss - newLoss) < 1e-7) break;
    currentLoss = newLoss;
  }

  return { weights: [...w], baselineRmse, rmse: currentLoss, trainingSize };
}
